/**
 * Procedural thruster exhaust shaders.
 * Renders a continuous flame shape on a flat PlaneGeometry using noise-driven
 * turbulence, Mach diamond banding, and HDR color gradients.
 * One draw call per thruster instead of 8 particle systems.
 *
 * Geometry: PlaneGeometry(FLAME_WIDTH, FLAME_LENGTH) rotated -PI/2 around X
 * to lie flat on XZ plane. UV.y=0 at nozzle (origin), flame extends along +Z.
 * Camera looks down Y-axis from Y=50.
 *
 * All visual constants are driven by uniforms (uFlame*) so the Particle Editor
 * can tweak them in real-time. Default values match DEFAULT_THRUSTER_FLAME.
 */

// ---- 3D Simplex Noise GLSL (Ashima/Stefan Gustavson) ----
// Same implementation as planetShaders.ts — shared across all procedural shaders

const NOISE_3D = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`;

// ---- Geometry constants ----

/** Flame plane width in local units. */
export const FLAME_WIDTH = 0.8;

/** Flame plane length in local units (extends along +Z from nozzle). */
export const FLAME_LENGTH = 4.0;

// ---- Vertex Shader ----

export const thrusterVertexShader = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// ---- Fragment Shader ----

export const thrusterFragmentShader = /* glsl */ `
${NOISE_3D}

uniform float uTime;
uniform float uIntensity;
uniform float uAfterburner;
uniform float uFlickerSeed;

// Configurable uniforms (driven by Particle Editor / particleEffectStore)
// Taper
uniform float uNozzleWidth;
uniform float uPeakWidth;
// Length
uniform float uFlameLen;
uniform float uFlameLenAB;
// Turbulent edges
uniform float uFlowSpeed;
uniform float uFlowSpeedBoost;
uniform float uDistortionBase;
uniform float uDistortionTipAdd;
// Mach diamonds
uniform float uMachFreqNormal;
uniform float uMachFreqAB;
uniform float uMachBandMin;
uniform float uMachBandRange;
// Internal turbulence
uniform float uTurbCoarseScale;
uniform float uTurbFineScale;
uniform float uTurbBase;
uniform float uTurbCoarseAmp;
uniform float uTurbFineAmp;
// Flicker
uniform float uFlickerGlobalFreq;
uniform float uFlickerGlobalAmp;
uniform float uFlickerLocalFreq;
uniform float uFlickerLocalAmp;
// Colors
uniform vec3 uNormalCore;
uniform vec3 uNormalMid;
uniform vec3 uNormalTip;
uniform vec3 uAbCore;
uniform vec3 uAbMid;
uniform vec3 uAbTip;
// HDR
uniform float uHdrMultNormal;
uniform float uHdrMultAB;
uniform float uNozzleBoostMax;

varying vec2 vUv;

void main() {
  // Early discard when fully inactive (avoid all noise computations)
  if (uIntensity < 0.001) discard;

  // UV mapping: x = -0.5..0.5 (lateral), y inverted so 0 = nozzle (near ship), 1 = tip
  // PlaneGeometry rotated -PI/2 around X flips Y->Z, putting UV.y=1 at z=0 (origin).
  // Invert so nozzle (ty=0) is at the origin and flame extends outward.
  float cx = vUv.x - 0.5;     // Center x: -0.5 to 0.5
  float ty = 1.0 - vUv.y;     // Tip progress: 0 = nozzle, 1 = tip

  // ---- 1. Flame silhouette (tapered envelope) ----
  float flameLength = mix(uFlameLen, uFlameLenAB, uAfterburner);
  float tipProgress = ty / flameLength;

  // Discard beyond flame tip
  if (tipProgress > 1.0) discard;

  // Bell-shaped taper: narrow at nozzle, flares mid-flame, pinches at tip
  float taper = mix(uNozzleWidth, uPeakWidth, sin(tipProgress * 3.14159));

  // ---- 2. Turbulent edge distortion ----
  // 3D noise scrolling in flow direction (-Y over time)
  float flowSpeed = uFlowSpeed + uAfterburner * uFlowSpeedBoost;
  float noiseX = cx * 4.0 + uFlickerSeed * 7.0;
  float noiseY = ty * 6.0 - uTime * flowSpeed;
  float noiseZ = uTime * 2.0 + uFlickerSeed * 13.0;
  float edgeNoise = snoise(vec3(noiseX, noiseY, noiseZ));

  // Distortion increases toward tip (flame breaks apart)
  float distortionStrength = uDistortionBase + tipProgress * uDistortionTipAdd;
  float distortedX = abs(cx) + edgeNoise * distortionStrength;

  // Soft alpha falloff from center to edge
  float edgeAlpha = 1.0 - smoothstep(taper * 0.6, taper, distortedX);

  // Discard outside flame boundary
  if (edgeAlpha < 0.01) discard;

  // ---- 3. Mach diamonds ----
  // Sinusoidal bright/dim bands along the jet axis
  float machFreq = mix(uMachFreqNormal, uMachFreqAB, uAfterburner);
  float machPhase = ty * machFreq - uTime * 5.0;
  float machBands = uMachBandMin + uMachBandRange * sin(machPhase);
  // Fade mach diamonds at the tip
  machBands = mix(machBands, 1.0, smoothstep(0.6, 1.0, tipProgress));

  // ---- 4. Internal turbulence ----
  // Coarse swirls
  float turbCoarse = snoise(vec3(
    cx * uTurbCoarseScale + uFlickerSeed,
    ty * 4.0 - uTime * 7.0,
    uTime * 1.5
  ));
  // Fine eddies
  float turbFine = snoise(vec3(
    cx * uTurbFineScale + uFlickerSeed * 3.0,
    ty * 10.0 - uTime * 12.0,
    uTime * 3.5
  ));
  float turbulence = uTurbBase + turbCoarse * uTurbCoarseAmp + turbFine * uTurbFineAmp;

  // ---- 5. High-frequency flicker ----
  // Global brightness oscillation at irrational frequencies
  float flickerGlobal = (1.0 - uFlickerGlobalAmp) + uFlickerGlobalAmp * sin(uTime * uFlickerGlobalFreq + uFlickerSeed * 100.0);
  // Localized flicker along flame length
  float flickerLocal = (1.0 - uFlickerLocalAmp) + uFlickerLocalAmp * sin(uTime * uFlickerLocalFreq + ty * 20.0 + uFlickerSeed * 50.0);
  float flicker = flickerGlobal * flickerLocal;

  // ---- 6. Color gradient ----
  // Interpolate along flame length
  vec3 normalColor = mix(uNormalCore, uNormalMid, smoothstep(0.0, 0.4, tipProgress));
  normalColor = mix(normalColor, uNormalTip, smoothstep(0.4, 1.0, tipProgress));

  vec3 abColor = mix(uAbCore, uAbMid, smoothstep(0.0, 0.4, tipProgress));
  abColor = mix(abColor, uAbTip, smoothstep(0.4, 1.0, tipProgress));

  // Blend between normal and afterburner palettes
  vec3 flameColor = mix(normalColor, abColor, uAfterburner);

  // ---- 7. HDR assembly ----
  float brightness = edgeAlpha * machBands * turbulence * flicker;

  // HDR multiplier for bloom
  float hdrMult = mix(uHdrMultNormal, uHdrMultAB, uAfterburner);

  // Extra nozzle boost (bright hotspot at base)
  float nozzleBoost = 1.0 + uNozzleBoostMax * (1.0 - smoothstep(0.0, 0.15, tipProgress));

  // Tip fade
  float tipFade = 1.0 - smoothstep(0.7, 1.0, tipProgress);

  float finalBrightness = brightness * hdrMult * nozzleBoost * tipFade * uIntensity;

  vec3 finalColor = flameColor * finalBrightness;
  float finalAlpha = edgeAlpha * tipFade * uIntensity;

  // Discard near-zero alpha
  if (finalAlpha < 0.01) discard;

  gl_FragColor = vec4(finalColor, finalAlpha);
}
`;
